import { first } from 'rxjs/operators';
import { ExpenseService, Expense } from '@app/_components/expense';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ExpenseComponent } from '../expense/expense.component';
import { ExpenseAdvanceSearchComponent } from '../expense-advance-search/expense-advance-search.component';
import {Component, Input, OnInit, ViewChild, OnDestroy, HostListener, AfterViewInit, ElementRef, ChangeDetectorRef, Inject, Optional} from '@angular/core';
import { AlertService } from '@app/_services';
import { BreezeWaysService } from '@app/_services/breezeways.service';
import { DataReportService } from '@app/_components/data-report/data-report.service';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import Utils from '@app/_components/_shared/utils';
import { PropertyService } from '@app/_components/property/property.service';
import {ImportExportComponent} from '@app/_components/_helpers/import-export/import-export.component';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Location } from '@angular/common';

@Component({
    selector: 'app-expense-list',
    templateUrl: './expense-list.component.html',
    styleUrls: ['./expense-list.component.css'],
    providers: [MessageService, ConfirmationService],
    // make column heading sticky
    styles: [`
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
        }

        @media screen and (max-width: 64em) {
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                top: 99px;
            }
        }
`],
    standalone: false
})
export class ExpenseListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(ImportExportComponent) importExportComponent: ImportExportComponent;
  @ViewChild('pt') table: any;
	properties: any;
	expenseVendors: any;
	shortTermRentalId: any[] = [];
	hasRouteParams: boolean = false; // Flag to track if route params exist
	routeParamsApplied: boolean = false; // Flag to track if route params have been applied
	initialLoadSkipped: boolean = false; // Flag to track if we've skipped the initial load

	constructor(
		public expenseService: ExpenseService,
		private alertService: AlertService,
		private messageService: MessageService,
		private propertyService: PropertyService,
		private confirmationService: ConfirmationService,
		private dialog: MatDialog,
		private cdr: ChangeDetectorRef,
		private activatedRoute: ActivatedRoute,
		private location: Location,
		private router: Router,
		private breezeWaysService: BreezeWaysService,
		private dataReportService: DataReportService,
		@Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
		@Optional() private dialogRef: MatDialogRef<ExpenseListComponent>
	) {
		this.statuses = expenseService.statuses;
		this.expenseVendors = expenseService.vendors;
	}

	get Statuses() {
		return this.statuses;
	}



	// data
	@Input() showFilter = true;
	@Input() showPagination = true;
	@Input() outlineNewButton = true;
	expenses: any;
	expense: Expense;
	selectedExpenses: Expense[] = []; // Multiple selected rows
	editingExpense: Expense | null = null; // Separate property for edit mode (for PrimeNG compatibility)
	editingExpenseIds: number[] = []; // Track multiple editing rows
	private editingExpensesMap: Map<number, Expense> = new Map(); // Map to store all editing expenses

	// paging
	first = 0;
	rows = 50;

	// list
	datasource: any;

	recordTotal: number = 0;
	lazyLoadEvent: LazyLoadEvent;
	loading = true;
	multiSortMeta = [];

	// forms
	formLoading = false;
	exportLoading = false;

	statuses: any;
	expenseDateRange: Date[] = [];
	stmtBilledDateRange: Date[] = [];
	showToStmtOptions: any[] = [
		{ name: 'All', value: null },
		{ name: 'Yes', value: true },
		{ name: 'No', value: false }
	];

	showToStmtOptionsWithoutAll: any[] = [
		{ name: 'Yes', value: true },
		{ name: 'No', value: false }
	];

	clonedExpenses: { [key: string]: Expense } = {};
	contextMenuItems: any[] = [];
	contextMenuExpense: any = null;

	// Bulk edit properties
	bulkEditField: string = '';
	bulkEditData: any = {
		status: null,
		expenseCategory: null,
		showToStmt: null,
		stmtBilledDate: null,
		name: null
	};
	bulkEditMenuItems: any[] = [];
	showBulkEditDialog: boolean = false;
	showToStmt: any = null;

	isTasksSyncing = false;
	showSyncBreezewayCustomDialog = false;
	customSyncDays: number | null = 60;
	forReconciliation: boolean = true;
	autoPayJobName = 'AutoPayReservationBalanceJob';
	autoPayFireTimes: { prevFireTime?: string; nextFireTime?: string } = {};
	autoPayFireTimesLoading = false;
	private findExpenseInArray(expenseId: number): Expense | undefined {
		return this.expenses.find((e: any) => e.expenseId === expenseId);
	}

	private prepareExpenseForEdit(expense: Expense): Expense {
		const expenseToEdit = this.findExpenseInArray(expense.expenseId) || expense;
		if (expenseToEdit.stmtBilledDate) {
			expenseToEdit.stmtBilledDate = this.toLocal(expenseToEdit.stmtBilledDate);
		}
		return expenseToEdit;
	}

	private clearEditState(): void {
		this.editingExpense = null;
		this.editingExpenseIds = [];
	}

	onDateSelect(date: Date, field: string) {
		const formatted = this.formatDate(date);
		this.table.filter(formatted, field, 'in');
	}

	private formatDate(date: Date): string {
		return moment(date).format('YYYY-MM-DD');
	}

	private buildAutoPayReferenceId(): string {
		const today = moment().format('YYYY-MM-DD');
		return `QUARTZ_JOB_${this.autoPayJobName}_${today}`;
	}

	private loadAutoPayFireTimes(): void {
		this.autoPayFireTimesLoading = true;
		const referenceId = this.buildAutoPayReferenceId();
		this.dataReportService.getReports({ referenceId, page: 0, size: 5, sort: 'lastUpdate,desc' }).subscribe({
			next: (res: any) => {
				const reports = res?.data?.qualityTests || res?.data?.dataReports || res?.dataReports || [];
				const report = reports?.[0];
				let meta: any = report?.metadata;
				if (typeof meta === 'string') {
					try {
						meta = JSON.parse(meta);
					} catch {
						meta = null;
					}
				}
				this.autoPayFireTimes = {
					prevFireTime: meta?.prevFireTime,
					nextFireTime: meta?.nextFireTime
				};
				this.autoPayFireTimesLoading = false;
			},
			error: () => {
				this.autoPayFireTimes = {};
				this.autoPayFireTimesLoading = false;
			}
		});
	}

	formatAutoPayFireTime(timeIso?: string): string {
		if (!timeIso) return '-';
		try {
			const d = new Date(timeIso);
			const day = String(d.getDate()).padStart(2, '0');
			const monthShort = d.toLocaleString('en-US', { month: 'short' });
			const hours = String(d.getHours()).padStart(2, '0');
			const minutes = String(d.getMinutes()).padStart(2, '0');
			const seconds = String(d.getSeconds()).padStart(2, '0');
			return `${day}-${monthShort} ${hours}:${minutes}:${seconds}`;
		} catch {
			return '-';
		}
	}


	private getExpensesToEdit(): Expense[] {
		if (this.selectedExpenses.length > 0) return this.selectedExpenses;
		if (this.selectedExpenseForContextMenu) return [this.selectedExpenseForContextMenu];
		if (this.table?.selection) {
			return Array.isArray(this.table.selection) ? this.table.selection : [this.table.selection];
		}
		return [];
	}

	columns: any[] = [
	{ field: 'expenseId', header: 'Id', width: 'auto',showSort: true },
	{ field: 'shortTermRentalId', header: 'Property Code', width: 'auto',showSort: true },
	{ field: 'expenseDate', header: 'Expense Date', width: 'auto',showSort: true },
	{ field: 'stmtBilledDate', header: 'StmtBilled Date', width: 'auto', showSort: true },
	{ field: 'showToStmt', header: 'On Statement?', width: 'auto', showSort: true },
	{ field: 'status', header: 'Status', width: 'auto', showSort: true },
	{ field: 'expenseCategory', header: 'Category', width: 'auto', showSort: true },
	{ field: 'name', header: 'Task', width: 'auto', showSort: true },
	{ field: 'paidByKbm', header: 'KBM Paid', width: 'auto', showSort: true },
	{ field: 'paidByOwner', header: 'Owner Paid', width: 'auto', showSort: true },
	{ field: 'expenseAmount', header: 'Total', width: 'auto', showSort: true },
	{ field: 'stmtExpenseAmount', header: 'Statement Amount', width: 'auto', showSort: false },
	{ field: 'discrepancyAmount', header: 'Discrepancy', width: 'auto', showSort: false },
	{ field: 'laborPaidByKbm', header: 'Labor (KBM Paid)', width: 'auto', showSort: true },
	{ field: 'laborPadiByOwner', header: 'Labor (Owner Paid)', width: 'auto', showSort: true },
	{ field: 'materialsPaidByKbm', header: 'Materials (KBM Paid)', width: 'auto', showSort: true },
	{ field: 'materialsPaidByOwner', header: 'Materials (Owner Paid)', width: 'auto', showSort: true },
	{ field: 'thirdPartExpensePaidByKbm', header: 'Third-Party (KBM Paid)', width: 'auto', showSort: true },
	{ field: 'thirdPartExpensePaidByOwner', header: 'Third-Party (Owner Paid)', width: 'auto', showSort: true },
	// { field: 'priority', header: 'Priority', width: 'auto',showSort: true },
	{ field: 'assignedToName', header: 'AssignedTo', width: 'auto',showSort: true },
	{ field: 'expenseVendor', header: 'Vendor', width: 'auto',showSort: true },
		{ field: 'expenseVendorId', header: 'VendorId', width: 'auto', showSort: true },
		{ field: 'createDate', header: 'Create Date', width: 'auto', showSort: true },
		{ field: 'lastUpdate', header: 'Last Update', width: 'auto', showSort: true }
  ];

  get displayColumns(): any[] {
	if (this.forReconciliation) {
		return this.columns;
	}
	return this.columns.filter(c => c.field !== 'discrepancyAmount');
  }


	/*********
	 * Lazy Load All Data
	 *
	 */
  eventFilter: LazyLoadEvent = {};

	ngOnInit(): void {
		this.fetchProperties();
		this.initializeContextMenu();
		this.initializeBulkEditMenu();
		this.loadAutoPayFireTimes();
		try {
			// default sort of list
			this.multiSortMeta = [];
			this.multiSortMeta.push({ field: 'expenseDate', order: -1 });
		}
		catch (error) {
			console.log('Error: ' + error);
		}

		// Read route query parameters synchronously if not in dialog mode
		if (!this.dialogData || Object.keys(this.dialogData).length === 0) {
			const params = this.activatedRoute.snapshot.queryParams;
			console.log('Route params in ngOnInit: ', params);
			
			if (params?.['showToStmt'] || params?.['shortTermRentalId'] || (params?.['startDate'] && params?.['endDate'])) {
				this.hasRouteParams = true;
			}

			if (params?.['showToStmt'] == 'true') {
				this.showToStmt = true;
			}

			if (params?.['forReconciliation'] == 'true') {
				this.forReconciliation = true;
			}
			
			if (params?.['shortTermRentalId']) {
				const rentalId = Array.isArray(params['shortTermRentalId'])
					? params['shortTermRentalId'].map(id => parseInt(id, 10))
					: [parseInt(params['shortTermRentalId'], 10)];
				this.shortTermRentalId = rentalId;
			}

			if (params?.['startDate'] && params?.['endDate']) {
				const startDate = moment(params['startDate']).toDate();
				const endDate = moment(params['endDate']).toDate();
				this.stmtBilledDateRange = [startDate, endDate];
			}
		}
	}

	ngAfterViewInit(): void {
		// Add scroll listener to table wrapper if it exists
		setTimeout(() => {
			this.setupTableScrollListener();
			this.applyInitialFilters();
		}, 500);
	}


	applyInitialFilters(): void {
		// Check if in dialog mode first
		if (this.dialogData && Object.keys(this.dialogData).length > 0) {
			console.log('applyInitialFilters from dialog: ', this.dialogData);

			if (this.dialogData.shortTermRentalId && this.dialogData.shortTermRentalId.length > 0) {
				this.shortTermRentalId = [...this.dialogData.shortTermRentalId];
				this.cdr.detectChanges();
				if (this.table) {
					setTimeout(() => {
						this.table.filter(this.dialogData.shortTermRentalId, 'shortTermRentalId', 'in');
					}, 100);
				}
			}

			if (this.dialogData.expenseDateRange && this.dialogData.expenseDateRange.length === 2) {
				this.expenseDateRange = this.dialogData.expenseDateRange;
				this.onExpenseDateRangeChange();
			}
			return;
		}

		// Apply filters from route query parameters (already read in ngOnInit)
		if (!this.hasRouteParams) {
			// No route params, let the normal lazy load proceed
			return;
		}

		// Initialize lazyLoadEvent if not exists
		if (!this.lazyLoadEvent) {
			this.lazyLoadEvent = {
				first: 0,
				rows: 100, // Match table configuration
				filters: {}
			} as LazyLoadEvent;
		}

		// Ensure filters object exists
		if (!this.lazyLoadEvent.filters) {
			this.lazyLoadEvent.filters = {};
		}

		// Apply shortTermRentalId filter
		if (this.shortTermRentalId && this.shortTermRentalId.length > 0) {
			this.cdr.detectChanges();
			if (this.table) {
				// Apply filter to table
				this.table.filter(this.shortTermRentalId, 'shortTermRentalId', 'in');
			}
			// Add to lazyLoadEvent filters
			this.lazyLoadEvent.filters['shortTermRentalId'] = {
				value: this.shortTermRentalId,
				matchMode: 'in'
			};
		}

		if (this.stmtBilledDateRange && this.stmtBilledDateRange.length === 2) {
			const startDate = moment(this.stmtBilledDateRange[0]).format('yyyy-MM-DD');
			const endDate = moment(this.stmtBilledDateRange[1]).format('yyyy-MM-DD');
			this.lazyLoadEvent.filters['stmtBilledDateStart'] = {
				value: startDate,
				matchMode: 'gte'
			};
			this.lazyLoadEvent.filters['stmtBilledDateEnd'] = {
				value: endDate,
				matchMode: 'lte'
			};
		}

		if (this.expenseDateRange && this.expenseDateRange.length === 2) {
			const startDate = moment(this.expenseDateRange[0]).format('yyyy-MM-DD');
			const endDate = moment(this.expenseDateRange[1]).format('yyyy-MM-DD');
			this.lazyLoadEvent.filters['expenseDateStart'] = {
				value: startDate,
				matchMode: 'gte'
			};
			this.lazyLoadEvent.filters['expenseDateEnd'] = {
				value: endDate,
				matchMode: 'lte'
			};
		}

		if (this.showToStmt !== null && this.showToStmt !== undefined) {
			this.table?.filter([this.showToStmt], 'showToStmt', 'in');

			if (this.showToStmt === true) {
				this.lazyLoadEvent.filters['showToStmt'] = {
					value: [true],
					matchMode: 'in'
				};
			}
		}


		// Mark route params as applied and trigger single API call
		this.routeParamsApplied = true;
		if (this.table) {
			setTimeout(() => {
				// Ensure lazyLoadEvent has all required properties
				if (!this.lazyLoadEvent.multiSortMeta || this.lazyLoadEvent.multiSortMeta.length === 0) {
					this.lazyLoadEvent.multiSortMeta = [];
					this.lazyLoadEvent.multiSortMeta.push({ field: 'expenseDate', order: -1 });
				}
				console.log('Calling loadLazy with route params filters: ', JSON.stringify(this.lazyLoadEvent.filters));
				this.loadLazy(this.lazyLoadEvent);
			}, 200);
		}
	}

	get isDialogMode(): boolean {
		// Check if dialogRef exists and dialogData has actual content (not just empty object)
		return !!(this.dialogRef && this.dialogData && Object.keys(this.dialogData).length > 0);
	}

	closeDialog(): void {
		if (this.dialogRef) {
			this.dialogRef.close();
		}
	}

	setupTableScrollListener(): void {
	}

	initializeContextMenu(): void {
		this.updateContextMenu();
	}

	updateContextMenu(): void {
		const menuItems: any[] = [
			{
				label: 'Edit',
				icon: 'pi pi-pencil',
				command: () => {
					const expensesToEdit = this.getExpensesToEdit();
					if (expensesToEdit.length === 0) return;

					expensesToEdit.length === 1
						? this.onRowEditInit(expensesToEdit[0])
						: this.onMultipleRowsEditInit(expensesToEdit);
				}
			}
		];

		if (this.selectedExpenses.length > 0) {
			menuItems.push({
				label: 'Cancel Selection',
				icon: 'pi pi-times',
				command: () => {
					if (this.editingExpenseIds.length > 0) {
						this.onMultipleRowsCancel();
					}
					this.selectedExpenses = [];
					this.cdr.detectChanges();
				}
			});
		}

		this.contextMenuItems = menuItems;
	}

	selectedExpenseForContextMenu: Expense | null = null;

	onRowContextMenu(event: MouseEvent, contextMenu: any, expense: any): void {
		event.preventDefault();
		event.stopPropagation();
		
		this.selectedExpenseForContextMenu = expense;
		
		if (this.table?.selectedExpenses?.length > 0) {
			this.table.selection = this.selectedExpenses;
		}
		
		// Update context menu to reflect current selection state
		this.updateContextMenu();
		
		contextMenu.show(event);
	}

	onEditSelectedRows(): void {
		if (!this.selectedExpenses?.length) return;

		if (this.selectedExpenses.length > 100) {
			this.messageService.add({
				severity: 'warn',
				summary: 'Bulk Edit Limit',
				detail: 'You can edit only 100 rows at a time'
			});
			return;
		}

		this.editingExpenseIds = this.selectedExpenses.map(e => e.expenseId);
		setTimeout(() => {
			this.selectedExpenses.forEach(expense => {
				this.table?.initRowEdit(expense);
			});
		});
	}

	
	



	onRowClick(event: MouseEvent, expense: any) {
		// Don't open popup if clicking on checkbox
		if ((event.target as HTMLElement).closest('p-tablecheckbox, .p-checkbox')) {
			return; // Ignore checkbox clicks - let selection happen
		}

		// Don't open popup if any rows are in edit mode
		if (this.editingExpenseIds.length > 0) {
			return;
		}

		// Prevent selection when clicking on the row (not checkbox)
		event.stopPropagation();
		event.preventDefault();

		// Remove from selection if it was selected by PrimeNG's pSelectableRow
		setTimeout(() => {
			const wasSelected = this.selectedExpenses.some(e => e.expenseId === expense.expenseId);
			if (wasSelected) {
				this.selectedExpenses = this.selectedExpenses.filter(e => e.expenseId !== expense.expenseId);
				if (this.table) {
					this.table.selection = this.selectedExpenses;
				}
				this.cdr.detectChanges();
			}
		}, 0);

		// Open edit popup
		this.onOpenFormEdit(expense);
	}


	onRowDoubleClick(event: MouseEvent, expense: Expense): void {
		if (this.editingExpenseIds.includes(expense.expenseId)) return;
		
		event.preventDefault();
		event.stopPropagation();
		this.onOpenFormEdit(expense);
	}

	isRowEditable(expense: Expense): boolean {
		return this.editingExpenseIds.includes(expense.expenseId);
	}


	onRowEditInit(expense: Expense): void {
		const expenseToEdit = this.prepareExpenseForEdit(expense);
		
		this.clonedExpenses[expense.expenseId.toString()] = { ...expenseToEdit };
		this.editingExpenseIds = [expense.expenseId];
		this.editingExpense = expenseToEdit;

		this.table?.initRowEdit(this.editingExpense);
		this.cdr.detectChanges();
	}

	onMultipleRowsEditInit(expenses: Expense[]): void {
		this.editingExpenseIds = [];
		
		// Process all expenses in a single pass
		const expensesToEdit = expenses.map(expense => {
			const expenseInArray = this.findExpenseInArray(expense.expenseId) || expense;
			
			this.clonedExpenses[expenseInArray.expenseId.toString()] = { ...expenseInArray };
			this.editingExpenseIds.push(expenseInArray.expenseId);
			
			if (expenseInArray.stmtBilledDate) {
				expenseInArray.stmtBilledDate = this.toLocal(expenseInArray.stmtBilledDate);
			}
			
			return expenseInArray;
		});

		if (expensesToEdit.length === 0) return;

		this.editingExpense = expensesToEdit[0];
		this.cdr.detectChanges();

		if (this.table) {
			setTimeout(() => {
				expensesToEdit.forEach(expense => {
					try {
						this.table.initRowEdit(expense);
					} catch (error) {
						console.warn('Error initializing row edit for expense:', expense.expenseId, error);
					}
				});
				this.editingExpense = expensesToEdit[0];
				this.cdr.detectChanges();
			}, 50);
		}
	}


	onRowEditSave(expense: Expense): void {
		if (this.editingExpenseIds.length > 1) {
			this.onMultipleRowsSave();
			return;
		}
		
		const expenseToSave = { ...expense };
		if (expenseToSave.stmtBilledDate instanceof Date) {
			expenseToSave.stmtBilledDate = this.toUTC(expenseToSave.stmtBilledDate);
		}
		
		this.expenseService.update(expenseToSave as Expense).subscribe(
			(res: any) => {
				this.messageService.add({ 
					severity: 'success', 
					summary: 'Success', 
					detail: 'Expense is updated' 
				});
				
				this.clearEditState();
				
				const expenseIndex = this.expenses.findIndex((e: any) => e.expenseId === expense.expenseId);
				if (expenseIndex !== -1 && res.data?.expense) {
					this.expenses[expenseIndex] = res.data.expense;
				}
				
				delete this.clonedExpenses[expense.expenseId.toString()];
				this.cdr.detectChanges();
			},
			(err: any) => {
				this.messageService.add({ 
					severity: 'error', 
					summary: 'Error', 
					detail: 'Expense is not updated' 
				});
				this.clearEditState();
				this.cdr.detectChanges();
			}
		);
	}

	onMultipleRowsSave(): void {
		if (!this.table || this.editingExpenseIds.length === 0) return;
		
		this.loading = true;
		
		// Get all expenses that are being edited and format for bulk update API
		const bulkUpdateData = this.editingExpenseIds.map(expenseId => {
			const expense = this.expenses.find((e: any) => e.expenseId === expenseId);
			if (!expense) return null;
			
			// Cast to any to access all properties
			const exp: any = expense;
			const updateData: any = {
				expenseId: expense.expenseId
			};

			// Include all required fields from the edited expense
			// Status
			updateData.status = exp.status || null;

			// Expense Category
			updateData.expenseCategory = exp.expenseCategory || null;

			// Show To Stmt
			updateData.showToStmt = exp.showToStmt !== undefined ? exp.showToStmt : null;

			// Stmt Billed Date - convert to YYYY-MM-DD format
			if (exp.stmtBilledDate) {
				// If it's a Date object, convert it, otherwise parse it
				const date = exp.stmtBilledDate instanceof Date 
					? exp.stmtBilledDate 
					: new Date(exp.stmtBilledDate);
				updateData.stmtBilledDate = moment(date).format('YYYY-MM-DD');
			} else {
				updateData.stmtBilledDate = null;
			}

			// Name
			updateData.name = exp.name || null;

			return updateData;
		}).filter(expense => expense !== null);
		
		if (bulkUpdateData.length === 0) {
			this.loading = false;
			return;
		}
		
		// Use bulk update API
		this.expenseService.bulkUpdate(bulkUpdateData).subscribe(
			(res: any) => {
				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: `Bulk update completed.`,
					life: 3000
				});

				// Reload the data
				this.loadLazy(this.lazyLoadEvent);
				
				// Clear selection and edit state
				this.selectedExpenses = [];
				if (this.table) {
					this.table.selection = [];
				}
				this.clearEditState();
				
				// Clear cloned expenses
				this.editingExpenseIds.forEach(expenseId => {
					delete this.clonedExpenses[expenseId.toString()];
				});

				this.loading = false;
				this.cdr.detectChanges();
			},
			(err: any) => {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Bulk update failed',
					life: 3000
				});
				this.loading = false;
				this.cdr.detectChanges();
			}
		);
	}



	onRowEditCancel(expense: Expense, index: number): void {
		if (this.editingExpenseIds.length > 1) {
			this.onMultipleRowsCancel();
			return;
		}
		
		const clonedExpense = this.clonedExpenses[expense.expenseId];
		if (clonedExpense) {
			this.expenses[index] = { ...clonedExpense };
			delete this.clonedExpenses[expense.expenseId];
		}
		
		this.table?.cancelRowEdit(expense);
		this.clearEditState();
		this.cdr.detectChanges();
	}

	onMultipleRowsCancel(): void {
		if (!this.table) return;
		
		this.selectedExpenses.forEach(expense => {
			this.table.cancelRowEdit(expense);
		});
		this.clearEditState();
	}


	fetchProperties() {
		this.loading = true;
		if (!this.properties || this.properties === undefined || this.properties.length < 1) {
			try {
				this.propertyService.propertySummaryIndividualPropertyList()
					.subscribe(
						res => {
							// console.log('findAllPropertiesSummary: ' + JSON.stringify(res));
							this.properties = res.data.properties;
							this.loading = false;
						},
						err => {
							console.log('Feature-Update FAIL: ' + JSON.stringify(err));
							this.alertService.error(err);
							this.loading = false;
						},
						() => { });
			} catch (error) {
				console.log('Form Invalid: ' + JSON.stringify(error));
				this.loading = false;
				this.alertService.error(error);
			}
		}
		else {
			// console.log('properties are ' + this.properties);
			this.loading = false;
		}
	}

	/*********
	 * Delete Button
	 *
	 */
	onRowDelete(event: any) {
		console.log('onRowDelete: ' + JSON.stringify(event));
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete Id# ' + event.expenseId + '?',
			header: 'Confirm',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.formDelete(event);
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
			}
		});
	}
  loadLazy(event: LazyLoadEvent) {
		try {
			// Skip the initial automatic lazy load if route params exist but haven't been applied yet
			// This prevents the first API call without filters
			if (this.hasRouteParams && !this.routeParamsApplied && !this.initialLoadSkipped) {
				console.log('Skipping initial lazy load, waiting for route params to be applied');
				this.initialLoadSkipped = true;
				return;
			}

			this.loading = true;
			this.lazyLoadEvent = event;

			// Ensure filters object exists
			if (!event.filters) {
				event.filters = {};
			}

			// Merge route param filters if they exist and haven't been merged yet
			if (this.hasRouteParams && this.routeParamsApplied) {
				if (this.shortTermRentalId && this.shortTermRentalId.length > 0) {
					event.filters['shortTermRentalId'] = {
						value: this.shortTermRentalId,
						matchMode: 'in'
					};
				}

				if (this.stmtBilledDateRange && this.stmtBilledDateRange.length === 2) {
					const startDate = moment(this.stmtBilledDateRange[0]).format('yyyy-MM-DD');
					const endDate = moment(this.stmtBilledDateRange[1]).format('yyyy-MM-DD');
					event.filters['stmtBilledDateStart'] = {
						value: startDate,
						matchMode: 'gte'
					};
					event.filters['stmtBilledDateEnd'] = {
						value: endDate,
						matchMode: 'lte'
					};
				}

				if (this.expenseDateRange && this.expenseDateRange.length === 2) {
					const startDate = moment(this.expenseDateRange[0]).format('yyyy-MM-DD');
					const endDate = moment(this.expenseDateRange[1]).format('yyyy-MM-DD');
					event.filters['expenseDateStart'] = {
						value: startDate,
						matchMode: 'gte'
					};
					event.filters['expenseDateEnd'] = {
						value: endDate,
						matchMode: 'lte'
					};
				}

				if (this.showToStmt === true) {
					event.filters['showToStmt'] = {
						value: [true],
						matchMode: 'in'
					};
				}
			}

			/*
			*
			* Default Sort
			*/
			// https://stackoverflow.com/questions/679915/how-do-i-expense-for-an-empty-javascript-object
			// if we are NOT filtering and we are NOT sorting, apply a default sort
			if (Object.keys(event.filters).length === 0 && !event.multiSortMeta) {
				event.multiSortMeta = [];
				event.multiSortMeta.push({ field: 'expenseDate', order: -1 });
			}

			const pageNumber = Math.round(event.first / event.rows);
			this.expenseService.findByFilter(
				pageNumber, event.rows,
				event.sortField,
				event.sortOrder,
				event.multiSortMeta,
				event.globalFilter,
				event.filters).pipe(first()).subscribe(

					(res: any) => {
						this.datasource = res.data.reviews;
						this.recordTotal = res.page.recordTotal;
						this.expenses = res.data.expenses;
						console.log(JSON.stringify(res.page));
					},
					(err: any) => {
						console.log('reviews list error!: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.loading = false; }
				);

			if (this.datasource) {
				this.expenses = this.datasource.slice(event.first, (event.first + event.rows));
			}
		} catch (error) {
			console.log('loadLazy(' + event + ')' + error);
		}
	}


	/*********
	 * onRowSelect()
	 * Used to handle row selection (single click)
	 * No longer opens edit form - that's handled by double-click
	 */
	onRowSelect(event: any) {
		// Don't trigger row select when in edit mode
		if (this.editingExpenseIds.length > 0) {
			// If in edit mode, prevent selection by removing the row from selection
			const clickedExpense = event.data;
			if (clickedExpense) {
				this.selectedExpenses = this.selectedExpenses.filter(e => e.expenseId !== clickedExpense.expenseId);
				this.cdr.detectChanges();
			}
			return;
		}
		
		const clickedExpense = event.data;
		if (!clickedExpense) {
			return;
		}
		
		// Use setTimeout to ensure we're working with PrimeNG's updated selection state
		setTimeout(() => {
			// Check if the clicked row is already in the selection
			const isAlreadySelected = this.selectedExpenses.some(e => e.expenseId === clickedExpense.expenseId);
			
			if (!isAlreadySelected) {
				// Add the row to selection, preserving all existing selections
				this.selectedExpenses = [...this.selectedExpenses, clickedExpense];
			}
			
			// Sync with table's internal selection to ensure consistency
			if (this.table) {
				this.table.selection = this.selectedExpenses;
			}
			
			this.cdr.detectChanges();
		}, 0);
	}

	onRowUnselect(event: any): void {
		// Handle row unselection - PrimeNG should handle this via two-way binding,
		// but we'll ensure the row is removed from the array
		const unselectedExpense = event.data;
		if (unselectedExpense) {
			this.selectedExpenses = this.selectedExpenses.filter(e => e.expenseId !== unselectedExpense.expenseId);
			this.cdr.detectChanges();
		}
	}

	onHeaderCheckboxToggle(event: any): void {
		// Handle header checkbox toggle (select all)
		if (event.checked) {
			// All visible rows are selected
		} else {
			// All rows are deselected
		}
	}

	// handled in rate-bucket-form
	formAdd() { }


	/*********
	 * formUpdate()
	 * Used for row editing
	 */
	formUpdate(expense: Expense) {
		try {
			this.formLoading = true;
			this.expenseService.update(expense)
				.subscribe(
					(res: any) => {
						console.log('Rate-Bucket-Update SUCCESS: ' + JSON.stringify(res));
						this.expenses.map((expense, i) => {
							if (expense.expenseId === res.data.expense.expenseId) {
								this.expenses[i] = res.data.expense;
							}
						});
						// this.alertService.success(res, true);
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
					},
					(err: any) => {
						console.log('Rate-Bucket-Update FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: JSON.stringify(err), life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	/*********
	 * formDelete()
	 * Used for row editing
	 */
	formDelete(expense: any) {
		try {
			// stop here if form is invalid
			console.log('Delete Submitted: ' + JSON.stringify(expense));

			this.formLoading = true;
			this.expenseService.delete(expense.expenseId)
				.subscribe(
					(res: any) => {
						console.log('Rate-Bucket-Delete SUCCESS: ' + JSON.stringify(res));
						this.expenses.splice(this.expenses.indexOf(expense), 1);
						this.recordTotal = this.expenses.length;
						this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Expense ' + expense.expenseId + ' Deleted', life: 3000 });
					},
					(err: any) => {
						console.log('Rate-Bucket-Delete  FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'Expense ' + expense.expenseId + ' could not be deleted.  Be sure there are no assigned properties to this bucket.', life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

  onBreezewayExpense() {

   /* // tslint:disable-next-line:max-line-length
    const numbers = [88 , 90 , 92 , 93 , 96 , 97 , 98 , 100 , 101 , 102 , 108 , 110 , 117 , 119 , 121 , 122 , 123 , 125 , 129 , 130 , 131 , 133 , 137 , 138 , 141 , 142 , 146 , 149 , 152 , 155 , 160 , 163 , 165 , 173 , 178 , 179 , 204 , 207 , 210 , 218 , 223 , 226 , 240 , 258 , 261 , 269 , 278 , 282 , 287 , 288 , 294 , 297 , 302 , 303 , 305 , 311 , 317 , 323 , 326 , 329 , 332 , 335 , 338 , 341 , 344 , 347 , 352 , 355 , 358 , 364 , 366 , 369 , 380 , 383 , 384 , 387 , 390 , 393 , 396 , 399 , 402 , 405 , 408 , 414 , 417 , 419 , 423 , 429 , 432 , 435 , 438 , 443 , 449 , 452 , 453 , 455 , 461 , 462 , 465 , 468 , 476 , 485 , 489 , 492 , 497 , 506 , 508 , 509 , 511 , 512 , 520 , 525 , 534 , 535 , 537 , 545 , 551 , 554 , 559 , 560 , 568 , 571 , 572 , 574 , 575 , 581 , 587 , 590 , 593 , 612 , 618 , 620 , 621 , 624 , 630 , 632 , 641 , 650 , 651 , 654 , 671 , 672 , 675 , 677 , 699 , 708 , 720 , 725 , 726 , 729 , 735 , 737 , 740 , 743 , 752 , 758 , 765 , 773 , 776 , 779 , 783 , 785 , 788 , 804 , 807 , 809 , 810 , 815 , 816 , 818 , 819 , 823 , 829 , 848 , 866 , 875 , 878 , 885 , 888 , 891 , 894 , 895 , 898 , 909 , 918 , 921 , 925 , 928 , 934 , 937 , 940 , 949 , 952 , 955 , 963 , 966 , 972 , 981 , 984 , 990 , 993 , 996 , 1006 , 1009 , 1011 , 1017 , 1018 , 1021 , 1029 , 1035 , 1036 , 1039 , 1042 , 1043 , 1046 , 1049 , 1062 , 1065 , 1066 , 1069 , 1070 , 1072 ];
    for (let i = 5; i < numbers.length; i++){
      Utils.sleep(3000);
      this.expenseService.syncById(numbers[i]
      ).subscribe(

        (res: any) => {
          this.datasource = res.data.reviews;
          this.recordTotal = res.page.recordTotal;
          this.expenses = res.data.expenses;
          console.log(JSON.stringify(res.page));
        },
        (err: any) => {
          console.log('reviews list error!: ' + JSON.stringify(err));
          this.alertService.error(err);
        },
        () => { this.loading = false; }
      );
    }
*/
  }
	/*********
		 * Add New Button
		 *
		 */
	onOpenFormAdd() {

		const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.maxWidth = '60vw';
			dialogConfig.width = '40vw';

		// pass data, ie rateBuicket: this.shortTermRentalId,
		dialogConfig.data = {
			title: 'Add Expense',
			isDialogMode: true
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(ExpenseComponent, dialogConfig);

		// to receive the added element, subscribe to the eventEmitter we created 'doAdd' in the element to handle adding
		dialogRef.componentInstance.doAdd.subscribe(
			data => {
				console.log('Dialog output:', data);

				// when we doAdd in the element, call this parent and update the list so it shows the added element to the database
				this.addExpenseCB(data);

				// close the dialogBox
				// dialogRef.close();
			});
	}


	/*********
		* Edit Button
		*
		*/
	onOpenFormEdit(expense: Expense) {
		if (!this.isDialogMode) {
			this.location.go('/expense/' + expense.expenseId);
		}
		this.openExpenseEditDialog(expense);
	}

	/**
	 * Opens the expense edit dialog and wires URL / afterClosed. Used by onOpenFormEdit and by openExpenseFromRouteIfNeeded.
	 */
	private openExpenseEditDialog(expense: Expense): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.maxWidth = '60vw';
		dialogConfig.height = '100vh';
		dialogConfig.panelClass = 'ctm-fix-height-popup';
		dialogConfig.data = {
			expense: expense,
			isDialogMode: true,
			title: 'Edit Expense'
		};

		const dialogRef = this.dialog.open(ExpenseComponent, dialogConfig);

		dialogRef.componentInstance.doUpdate.subscribe(
			data => {
				console.log('Dialog output:', data);
				this.loadLazy(this.lazyLoadEvent);
			});

		dialogRef.componentInstance.doDelete.subscribe(
			data => {
				console.log('Dialog output:', data);
				this.loadLazy(this.lazyLoadEvent);
				dialogRef.close();
			});

		dialogRef.afterClosed().subscribe(() => {
			if (!this.isDialogMode) {
				this.location.go('/expense-list');
			}
		});
	}


	/**
	   *
	   * Dialog from Form Callbacks
	   * To edit the list show add/edit/delete in real time from the popup form
	   *
	   */
	addExpenseCB(expense: Expense) {
		console.log('Adding Expense: ' + JSON.stringify(expense));
		// if we don't have a expenses array yet...
		if (!this.expenses) {
			this.expenses = [];
		}
		// unshift adds to the front of the array (), push adds to the back
		this.expenses.unshift(expense);

		// no idea what this is but it is required to update the array
		this.expenses = [...this.expenses];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Expense ' + expense.expenseId + ' Added', life: 3000 });
	}

	updateExpenseCB(expense: Expense) {
		console.log('Updating Expense: ' + JSON.stringify(expense));
		let itemIndex = this.expenses.findIndex((item: any) => item.expenseId === expense.expenseId);
		this.expenses[itemIndex] = expense;

		// no idea what this is but it is required to update the array
		this.expenses = [...this.expenses];

		// this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Expense ' + expense.expenseId + ' Updated', life: 3000 });
	}

	deleteExpenseCB(expense: Expense) {
		console.log('Removing Expense: ' + expense);
		this.expenses.splice(this.expenses.indexOf(expense), 1);
		this.recordTotal = this.expenses.length;

		// no idea what this is but it is required to update the array
		this.expenses = [...this.expenses];

		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Expense ' + expense.expenseId + ' Deleted', life: 3000 });
	}

	/**********
	 *
	 * PAGINATOR FUNCTIONS
	 */
	next() { this.first = this.first + this.rows; }
	prev() { this.first = this.first - this.rows; }
	reset() { this.first = 0; }
	isLastPage(): boolean { return this.expenses ? this.first === (this.expenses.length - this.rows) : true; }
	isFirstPage(): boolean { return this.expenses ? this.first === 0 : true; }

	getStatusName(expense: any) {
		if (expense?.status) {
			const status = this.statuses.find((e: any) => e.value == expense.status);
			return status.name;
		}
		else {
			return '';
		}
	}

	reloadPage() {
		window.location.reload();
	}

	onAdvanceSearch() {
		try {
			const dialogConfig = new MatDialogConfig();

			// settings
			dialogConfig.disableClose = true;
			dialogConfig.autoFocus = true;
			dialogConfig.hasBackdrop = true;
			dialogConfig.maxWidth = "450px";
			dialogConfig.width = "400px";
			dialogConfig.panelClass = "expense-advance-search-dialog";

			// pass data
			dialogConfig.data = {
				advanceSearchData: {},
				properties: this.properties,
				title: 'Advanced Search'
			};

			// open the popup and get a reference to it called dialogRef
			const dialogRef = this.dialog.open(ExpenseAdvanceSearchComponent, dialogConfig);
			
			// Add body class to style datepicker popup (which is appended to body)
			document.body.classList.add('expense-advance-search-open');
			
			dialogRef.componentInstance.doSearchCB.subscribe(
				data => {
					console.log('Advanced Search Data:', data);
					// TODO: Apply search filters here
					// close the dialogBox
					dialogRef.close();
				});
			
			// Remove body class when dialog closes
			dialogRef.afterClosed().subscribe(() => {
				document.body.classList.remove('expense-advance-search-open');
			});
		} catch (error) {
			console.log('onAdvancedSearch error: ' + error);
		}
	}

	showPaginator()
	{
		return true;
	}

  getVendor(expense: any){
    if (expense?.expenseVendor) {
      const status = this.expenseService.vendors.find((e: any) => e.value == expense.expenseVendor);
      return status.name;
    }
    return 'KBM';
  }

  getExpenseCategory(expense: any){
    if (expense?.expenseCategory) {
      const status = this.expenseService.expenseCategories.find((e: any) => e.value == expense.expenseCategory);
      return status.name;
    }
    return 'TBD';
	}

  getStatus(expense: any){
    const status = this.expenseService.statuses.find((e: any) => e.value == expense.status);
    if (status){
      return status?.name;
    }
    return 'Draft';
  }

  getPriority(expense: any){
      const priority = this.expenseService.priorities.find((e: any) => e.value == expense.priority);
      if (priority){
        return priority.name;
      }
    return 'NORMAL';
  }

  getPropertyCode(shortTermRentalId: any) {
	if (shortTermRentalId) {
		const status = this.properties.find((e: any) => e.shortTermRentalId == shortTermRentalId);
		return status?.propertyCode || '';
	}
	else {
		return '';
	}
}

  getReconciliationRowClass(expense: any): string {
	if (!this.forReconciliation || expense == null) {
		return '';
	}
	const match = expense.match;
	if (match === true) {
		return 'reconcile-row-match-true';
	}
	if (match === false) {
		return 'reconcile-row-match-false';
	}
	return 'reconcile-row-match-null';
  }

  convertExpensesToFlatRows(expensesData: any): any[] {
    const result: any[] = [];

    for (const expense of expensesData) {
      // @ts-ignore
      const { expenseDetails = [], ...mainFields } = expense;

      if (expenseDetails.length === 0) {
        // No details, just push main expense as is
        result.push({ ...mainFields });
      } else {

          // const detailFields = this.flattenDetail(detail, idx + 1);
          // result.push({
          //   ...mainFields,
          //   ...detailFields
          // });

          // Merge ALL details into a single object
          // const mergedDetailFields = expenseDetails.reduce((acc: any, detail: any, idx: number) => {
          //   const detailFields = this.flattenDetail(detail, idx + 1);
          //   return { ...acc, ...detailFields };
          // }, {});

          result.push({
            ...mainFields,
  //          ...mergedDetailFields
          });


      }
    }

    return result;
  }

  flattenDetail(detail: any, index: number): any {
    const prefix = `expenseDetail_${index}_`;
    const flattened: any = {};

    for (const key in detail) {
      if (detail.hasOwnProperty(key)) {
        flattened[`${prefix}${key}`] = detail[key];
      }
    }

    return flattened;
  }

  exportAsExcelFile($event: any) {
    this.exportLoading = true;
    const exportEvent: LazyLoadEvent = {
      first: 0,
      rows: this.recordTotal || 999999, // Fetch all records
      sortField: this.lazyLoadEvent?.sortField || null,
      sortOrder: this.lazyLoadEvent?.sortOrder || null,
      multiSortMeta: this.lazyLoadEvent?.multiSortMeta || [],
      globalFilter: this.lazyLoadEvent?.globalFilter || null,
      filters: this.lazyLoadEvent?.filters || {}
    };

    if (Object.keys(exportEvent.filters).length === 0 && !exportEvent.multiSortMeta.length) {
      exportEvent.multiSortMeta = [];
      exportEvent.multiSortMeta.push({ field: 'expenseDate', order: -1 });
    }

    const pageNumber = 0;
    const pageSize = exportEvent.rows;

    this.expenseService.findByFilter(
      pageNumber,
      pageSize,
      exportEvent.sortField,
      exportEvent.sortOrder,
      exportEvent.multiSortMeta,
      exportEvent.globalFilter,
      exportEvent.filters
    ).pipe(first()).subscribe(
      (res: any) => {
        try {
          const allExpenses = res.data.expenses || [];
          let data = [];
          
          allExpenses.forEach((e: any) => {
            let row = {
              Id: e.expenseId,
              PropertyCode: this.getPropertyCode(e.shortTermRentalId),
              ExpenseDate: this.formatDateForExport(e.expenseDate),
              StmtBilledDate: this.formatDateForExport(e.stmtBilledDate),
              ExpenseCategory: this.getExpenseCategory(e),
              Status: this.getStatus(e),
              Priority: this.getPriority(e),
              AssignedTo: e.assignedToName,
              VendorId: e.expenseVendorId,
              ExpenseURL: e.vendorReportUrl,
              Vendor: this.getVendor(e),
              Task: e.name,
              Description: e.description,
              createDate: this.formatDateForExport(e.createDate),
              lastUpdate: this.formatDateForExport(e.lastUpdate),
              topicId: e.topicId,
              createdByUserName: e.createdByUserName,
              workOrderCreateDate: this.formatDateForExport(e.workOrderCreateDate),
              completedByUserName: e.completedByUserName,
              completedDate: this.formatDateForExport(e.completedDate),
              requestedBy: e.requestedBy,
              scheduledDateTime: this.formatDateForExport(e.scheduledDateTime),
              startDateTime: this.formatDateForExport(e.startDateTime),
              vendorUpdateDateTime: this.formatDateForExport(e.vendorUpdateDateTime),
              showToStmt: e.showToStmt,
              amountPaidByOwner: e.amountPaidByOwner,
              amountPaidByKBM: e.amountPaidByKBM,
              expSubTotal: e.expSubTotal,
              expTax: e.expTax,
              expTotal: e.expTotal,
              expenseDetails: e.expenseDetails
            };
            data.push(row);
          });

          
          this.importExportComponent.exporData($event, this.convertExpensesToFlatRows(data), 'Expenses');
          
          this.messageService.add({
            severity: 'success',
            summary: 'Export Complete',
            detail: `Exported ${allExpenses.length} expenses to Excel`,
            life: 3000
          });
        } catch (error) {
          console.error('Error processing export data:', error);
          this.alertService.error('Error processing export data');
        } finally {
          this.exportLoading = false;
        }
      },
      (err: any) => {
        console.error('Error fetching expenses for export:', err);
        this.alertService.error('Error fetching expenses for export');
        this.exportLoading = false;
      }
    );
  }

  getDateTimeBasedOnTimeZone(date: any){
		return moment.utc(date).toDate()
	}

	/**
	   * Converts a local date/time to UTC (for saving to backend)
	   */
	  toUTC(date: Date | string | null): Date | null {
		if (!date) return null;
		return moment(date).utc().toDate();
	  }

	  /**
	   * Converts a UTC date/time to local (for displaying in UI)
	   */
	  toLocal(date: Date | string | null): Date | null {
		if (!date) return null;
		return moment(date).toDate();
	  }

  /**
   * Reusable method to handle date range filter changes
   * @param dateRange - Array of dates [startDate, endDate]
   * @param startDateFilterKey - Filter key for start date (e.g., 'expenseStartDate')
   * @param endDateFilterKey - Filter key for end date (e.g., 'expenseEndDate')
   */
  private applyDateRangeFilter(dateRange: Date[], startDateFilterKey: string, endDateFilterKey: string,loadLazy: boolean = true): void {
	if (!this.lazyLoadEvent) {
		this.lazyLoadEvent = {} as LazyLoadEvent;
	}

	this.lazyLoadEvent.filters = this.lazyLoadEvent.filters || {};

	if (dateRange && dateRange.length === 2) {
		// Format dates as mm/dd/yyyy
		const startDate = moment(dateRange[0]).format('yyyy-MM-DD');
		const endDate = moment(dateRange[1]).format('yyyy-MM-DD');

		this.lazyLoadEvent.filters[startDateFilterKey] = {
			value: startDate,
			matchMode: 'gte'
		};
		this.lazyLoadEvent.filters[endDateFilterKey] = {
			value: endDate,
			matchMode: 'lte'
		};
	} else {
		// Clear filters if range is cleared
		delete this.lazyLoadEvent.filters[startDateFilterKey];
		delete this.lazyLoadEvent.filters[endDateFilterKey];
	}

	// Reload the table with updated filters
	loadLazy ? this.loadLazy(this.lazyLoadEvent) : null;
  }

  onExpenseDateRangeChange(loadLazy: boolean = true): void {
	this.applyDateRangeFilter(this.expenseDateRange, 'expenseDateStart', 'expenseDateEnd', loadLazy);
  }

  onStmtBilledDateRangeChange(loadLazy: boolean = true): void {
	this.applyDateRangeFilter(this.stmtBilledDateRange, 'stmtBilledDateStart', 'stmtBilledDateEnd', loadLazy);
  }


	formatDateForExport(date: Date | string | null | undefined): string {
    if (!date) return '';
    try {
      return moment.utc(date).format('MM-DD-YYYY');
    } catch (error) {
      console.error('Error formatting date for export:', error, date);
      return '';
    }
  }

	// Removed scroll and resize listeners - buttons are now fixed in viewport

	/*********
	 * Bulk Edit Methods
	 *
	 */
	initializeBulkEditMenu(): void {
		// Create nested menu items with submenus
		this.bulkEditMenuItems = [
			{
				label: 'Status',
				icon: 'pi pi-tag',
				items: this.expenseService.statuses.map(status => ({
					label: status.name,
					command: () => this.selectFieldValue('status', status.value)
				}))
			},
			{
				label: 'Category',
				icon: 'pi pi-list',
				items: this.expenseService.expenseCategories.map(category => ({
					label: category.name,
					command: () => this.selectFieldValue('expenseCategory', category.value)
				}))
			},
			{
				label: 'Show To Stmt',
				icon: 'pi pi-eye',
				items: this.showToStmtOptionsWithoutAll.map(option => ({
					label: option.name,
					command: () => this.selectFieldValue('showToStmt', option.value)
				}))
			}
		];
	}

	onBulkEditClick(): void {
		if (this.selectedExpenses.length === 0) {
			this.messageService.add({
				severity: 'warn',
				summary: 'No Selection',
				detail: 'Please select at least one expense to edit',
				life: 3000
			});
			return;
		}
		
		// Open selected rows in edit mode
		if (this.selectedExpenses.length > 100) {
			this.messageService.add({
				severity: 'warn',
				summary: 'Bulk Edit Limit',
				detail: 'You can edit only 100 rows at a time'
			});
			return;
		}
		
		this.onMultipleRowsEditInit(this.selectedExpenses);
	}

	onCancelEditAndSelection(): void {
		// Cancel edit mode if any rows are being edited
		if (this.editingExpenseIds.length > 0) {
			this.onMultipleRowsCancel();
		}
		
		// Clear all selections
		this.selectedExpenses = [];
		if (this.table) {
			this.table.selection = [];
		}
		
		// Clear edit state
		this.clearEditState();
		
		// Reset bulk edit data
		this.bulkEditData = {
			status: null,
			expenseCategory: null,
			showToStmt: null,
			stmtBilledDate: null,
			name: null
		};
		this.bulkEditField = '';
		
		this.cdr.detectChanges();
	}

	selectFieldValue(field: string, value: any): void {
		this.bulkEditField = field;
		
		if (field === 'status') {
			this.bulkEditData.status = value;
		} else if (field === 'expenseCategory') {
			this.bulkEditData.expenseCategory = value;
		} else if (field === 'showToStmt') {
			this.bulkEditData.showToStmt = value;
		}
		
		// After selecting a value, show confirmation dialog
		setTimeout(() => {
			if (this.isBulkEditDataValid()) {
				this.confirmBulkUpdate();
			}
		}, 100);
	}

	openDatePickerDialog(): void {
		this.bulkEditField = 'stmtBilledDate';
		this.showBulkEditDialog = true;
	}

	openNameInputDialog(): void {
		this.bulkEditField = 'name';
		this.showBulkEditDialog = true;
	}

	isBulkEditDataValid(): boolean {
		if (this.bulkEditField === 'status') {
			return this.bulkEditData.status !== null && this.bulkEditData.status !== undefined;
		}
		if (this.bulkEditField === 'expenseCategory') {
			return this.bulkEditData.expenseCategory !== null && this.bulkEditData.expenseCategory !== undefined;
		}
		if (this.bulkEditField === 'showToStmt') {
			return this.bulkEditData.showToStmt !== null && this.bulkEditData.showToStmt !== undefined;
		}
		if (this.bulkEditField === 'stmtBilledDate') {
			return this.bulkEditData.stmtBilledDate !== null && this.bulkEditData.stmtBilledDate !== undefined;
		}
		if (this.bulkEditField === 'name') {
			return this.bulkEditData.name !== null && this.bulkEditData.name !== undefined && this.bulkEditData.name.trim() !== '';
		}
		return false;
	}

	confirmBulkUpdate(): void {
		if (!this.isBulkEditDataValid()) {
			return;
		}

		this.confirmationService.confirm({
			message: `Are you sure you want to update ${this.selectedExpenses.length} expense(s)?`,
			header: 'Confirm Bulk Update',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.applyBulkUpdate();
			},
			reject: () => {
				this.messageService.add({
					severity: 'info',
					summary: 'Canceled',
					detail: 'Bulk update was canceled',
					life: 3000
				});
			}
		});
	}

	applyBulkUpdate(): void {
		if (this.selectedExpenses.length === 0) {
			return;
		}

		this.loading = true;

		// Prepare the bulk update data array
		const bulkUpdateData = this.selectedExpenses.map(expense => {
			// Cast to any to access all properties
			const exp: any = expense;
			const updateData: any = {
				expenseId: expense.expenseId,
				expenseDate: expense.expenseDate,
			};

			// Include all required fields, using new value if being updated, otherwise existing value
			// Status
			if (this.bulkEditField === 'status' && this.bulkEditData.status !== null) {
				updateData.status = this.bulkEditData.status;
			} else {
				updateData.status = exp.status || null;
			}

			// Expense Category
			if (this.bulkEditField === 'expenseCategory' && this.bulkEditData.expenseCategory !== null) {
				updateData.expenseCategory = this.bulkEditData.expenseCategory;
			} else {
				updateData.expenseCategory = exp.expenseCategory || null;
			}

			// Show To Stmt
			if (this.bulkEditField === 'showToStmt' && this.bulkEditData.showToStmt !== null) {
				updateData.showToStmt = this.bulkEditData.showToStmt;
			} else {
				updateData.showToStmt = exp.showToStmt !== undefined ? exp.showToStmt : null;
			}

			// Stmt Billed Date
			if (this.bulkEditField === 'stmtBilledDate' && this.bulkEditData.stmtBilledDate !== null) {
				// Convert date to UTC format (YYYY-MM-DD)
				const date = this.bulkEditData.stmtBilledDate instanceof Date 
					? this.bulkEditData.stmtBilledDate 
					: new Date(this.bulkEditData.stmtBilledDate);
				updateData.stmtBilledDate = moment(date).format('YYYY-MM-DD');
			} else {
				updateData.stmtBilledDate = exp.stmtBilledDate ? moment(exp.stmtBilledDate).format('YYYY-MM-DD') : null;
			}

			// Expense Date
			if (this.bulkEditField === 'expenseDate' && this.bulkEditData.expenseDate !== null) {
				// Convert date to UTC format (YYYY-MM-DD)
				const date = this.bulkEditData.expenseDate instanceof Date
					? this.bulkEditData.expenseDate
					: new Date(this.bulkEditData.expenseDate);
				updateData.expenseDate = moment(date).format('YYYY-MM-DD');
			} else {
				updateData.expenseDate = exp.expenseDate ? moment(exp.expenseDate).format('YYYY-MM-DD') : null;
			}

			// Name
			if (this.bulkEditField === 'name' && this.bulkEditData.name !== null) {
				updateData.name = this.bulkEditData.name;
			} else {
				updateData.name = exp.name || null;
			}

			return updateData;
		});

		this.loading = true;
		this.expenseService.bulkUpdate(bulkUpdateData).subscribe(
			(res: any) => {
				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: `Bulk update completed.`,
					life: 3000
				});

				// Reload the data
				this.loadLazy(this.lazyLoadEvent);
				
				// Clear selection
				this.selectedExpenses = [];
				if (this.table) {
					this.table.selection = [];
				}

				// Close dialog and reset
				this.showBulkEditDialog = false;
				this.bulkEditData = {
					status: null,
					expenseCategory: null,
					showToStmt: null,
					stmtBilledDate: null,
					name: null
				};
				this.bulkEditField = '';

				this.loading = false;
				this.cdr.detectChanges();
			},
			(err: any) => {
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail: 'Bulk update failed',
					life: 3000
				});
				this.loading = false;
				this.cdr.detectChanges();
			}
		);
	}

	ngOnDestroy(): void {
		// Cleanup if needed
		this.editingExpense = null;
		this.editingExpenseIds = [];
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent): void {
		// Handle Ctrl+A to select all visible rows
		if (event.ctrlKey && event.key === 'a') {
			event.preventDefault();
			if (this.expenses && this.expenses.length > 0) {
				this.selectedExpenses = [...this.expenses];
				this.cdr.detectChanges();
			}
		}
	}

	clearFilters(): void {
		this.expenseDateRange = [];
		this.stmtBilledDateRange = [];
		this.eventFilter = {};
		this.loadLazy(this.eventFilter);
	}

	openStatement(event: Event, expense: any): void {
		event.stopPropagation();
		event.preventDefault();

		const statementId = expense?.stmtExpSummaryAdmin?.ownerStmtId;
		if (statementId) {
			const url = this.router.serializeUrl(this.router.createUrlTree(['/owner-statement', statementId]));
			window.open(url, '_blank');
		} else {
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: 'Statement not found',
				life: 3000
			});
		}
	}


	syncAllTasksToBreezwayLast45Days() {
		this.syncBreezewayByDays(45);
	}

	openSyncBreezewayCustomDialog() {
		this.customSyncDays = 60;
		this.showSyncBreezewayCustomDialog = true;
	}

	confirmSyncBreezewayCustom() {
		const days = this.customSyncDays != null && this.customSyncDays > 0 ? Math.floor(this.customSyncDays) : null;
		if (days == null || days < 1) {
			this.messageService.add({ severity: 'warn', summary: 'Invalid', detail: 'Please enter a valid number of days (1 or more).', life: 3000 });
			return;
		}
		this.showSyncBreezewayCustomDialog = false;
		this.syncBreezewayByDays(days);
	}

	private syncBreezewayByDays(days: number) {
		this.isTasksSyncing = true;
		this.breezeWaysService.syncExpenseByDays(days).subscribe(
			res => {
				this.isTasksSyncing = false;
				this.messageService.add({
					severity: 'success',
					summary: 'Success',
					detail: days === 45 ? 'Successfully synced tasks to Breezeway for last 45 days' : `Successfully synced tasks to Breezeway for last ${days} days`,
					life: 6000
				});
				this.loadLazy(this.lazyLoadEvent);
				this.loadAutoPayFireTimes();
			},
			err => {
				this.isTasksSyncing = false;
				console.log('Sync Breezeway expense FAIL: ' + JSON.stringify(err));
				this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || err?.message || 'Failed to sync tasks to Breezeway', life: 5000 });
			}
		);
	}
}
